// useNodelistTargetOptions.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useBase } from 'context/BaseContext';
import { useUser } from 'context/UserContext';

// Services
import { subscribeToNodesByDatabase } from 'services/nodes/NodeReadService';
import { subscribeToTypeCatalog } from 'services/catalog/TypeCatalogService';
import { resolveRelationTargetToNodeKey } from 'services/catalog/CatalogAdoptService';

// Utilities
import { getNodelistNodeTitle } from 'utilities/NodelistField';
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';
import {
  applyCatalogLiveRead,
  buildCatalogOptionValue,
  getNodeCatalogId,
  parseCatalogOptionValue,
} from 'utilities/PlatformCatalog';

/**
 * Subscribes to active records for a nodelist target type and maps them to
 * SelectInput-ready options (value/label/iconKey/note).
 * When the type has a non-empty platform catalog, merges catalog entries
 * (adopt-on-select via resolveOptionValue).
 *
 * @param {object} params
 * @param {string} params.targetType
 * @param {string|null} [params.baseKey]
 * @param {boolean} [params.enabled=true]
 * @returns {{
 *   options: object[],
 *   nodes: object[],
 *   nodesByKey: Map<string, object>,
 *   catalogById: Map<string, object>,
 *   isLoading: boolean,
 *   resolveOptionValue: function(string): Promise<string>,
 * }}
 */
const useNodelistTargetOptions = ({
  targetType,
  baseKey: baseKeyProp = null,
  enabled = true,
} = {}) => {
  const { getType } = useNodeTypes();
  const { activeBase } = useBase();
  const { user } = useUser();

  const [nodes, setNodes] = useState([]);
  const [catalogEntries, setCatalogEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const baseKey = baseKeyProp || activeBase?.key || null;
  const resolvedTargetType = String(targetType || '').trim();
  const typeDef = resolvedTargetType ? getType(resolvedTargetType) : null;

  useEffect(() => {
    if (!enabled || !resolvedTargetType || !baseKey) {
      setNodes([]);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    const unsubscribe = subscribeToNodesByDatabase(
      { type: resolvedTargetType, baseKey },
      (nextNodes) => {
        setNodes(Array.isArray(nextNodes) ? nextNodes : []);
        setIsLoading(false);
      },
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [enabled, resolvedTargetType, baseKey]);

  useEffect(() => {
    if (!enabled || !resolvedTargetType) {
      setCatalogEntries([]);
      return undefined;
    }

    return subscribeToTypeCatalog(resolvedTargetType, (rows) => {
      setCatalogEntries(Array.isArray(rows) ? rows : []);
    });
  }, [enabled, resolvedTargetType]);

  const catalogById = useMemo(() => {
    const map = new Map();
    catalogEntries.forEach((entry) => {
      const id = String(entry?.key || entry?.id || '').trim();
      if (id) map.set(id, entry);
    });
    return map;
  }, [catalogEntries]);

  const nodesByKey = useMemo(() => {
    const map = new Map();
    nodes.forEach((node) => {
      const key = String(node?.key || '').trim();
      if (!key) return;
      const catalogId = getNodeCatalogId(node);
      const entry = catalogId ? catalogById.get(catalogId) : null;
      map.set(key, applyCatalogLiveRead(node, entry, typeDef) || node);
    });
    return map;
  }, [catalogById, nodes, typeDef]);

  const options = useMemo(() => {
    const adoptedCatalogIds = new Set();
    const merged = [];

    nodesByKey.forEach((node, key) => {
      const catalogId = getNodeCatalogId(node);
      if (catalogId) adoptedCatalogIds.add(catalogId);
      const { iconKey } = resolveNodeIcon({ node, typeDef });
      merged.push({
        value: key,
        label: getNodelistNodeTitle(node),
        iconKey: iconKey || '',
        note: getNodeSubtitle(node) || '',
        node,
        catalogId: catalogId || null,
      });
    });

    // Empty catalog = opt-out (custom-only options already in merged).
    catalogEntries.forEach((entry) => {
      const catalogId = String(entry?.key || entry?.id || '').trim();
      if (!catalogId || adoptedCatalogIds.has(catalogId)) return;
      const title = String(entry?.title || '').trim() || catalogId;
      merged.push({
        value: buildCatalogOptionValue(catalogId),
        label: title,
        iconKey: typeDef?.iconKey || '',
        note: '',
        catalogId,
        isCatalogOption: true,
      });
    });

    return merged.sort((left, right) => (
      left.label.localeCompare(right.label, undefined, { sensitivity: 'base' })
    ));
  }, [catalogEntries, nodesByKey, typeDef]);

  const resolveOptionValue = useCallback(async (rawValue) => {
    const value = String(rawValue || '').trim();
    if (!value) return '';

    if (!parseCatalogOptionValue(value)) {
      return value;
    }

    if (!user || !baseKey || !resolvedTargetType) {
      throw new Error('Cannot adopt catalog entry without user and base');
    }

    return resolveRelationTargetToNodeKey({
      value,
      targetType: resolvedTargetType,
      user,
      baseKey,
      catalogById,
      typeDef,
    });
  }, [baseKey, catalogById, resolvedTargetType, typeDef, user]);

  return {
    options,
    nodes: [...nodesByKey.values()],
    nodesByKey,
    catalogById,
    isLoading,
    resolveOptionValue,
  };
};

export default useNodelistTargetOptions;
